import { DollarSign, Percent, Plus, Trash2 } from "lucide-react";
import React from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  CardTitle,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RequiredLabel,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import RichTextEditor from "@/components/rich-text-editor";
import { NumericInput } from "@/components/ui/numeric-input";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/context/i18n";
import { useInsuranceCompanies } from "@/pages/insurance-companies/insurance-companies.hooks";

import { TProductFormData } from "../../products.schema";
import { PaymentGatewayField } from "../payment-gateway-field";
import { SoulhubExternalProductIdField } from "../soulhub-external-product-id-field";
import { DynamicInputList } from "./dynamic-input-form";

export interface IProductsFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TProductFormData>;
  onSubmit: (values: TProductFormData) => void;
  isEditMode?: boolean;
}

export const InsuranceGroupLifeForm: React.FC<IProductsFormProps> = ({
  open,
  form,
  onSubmit,
  isEditMode,
}) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = React.useState("data");

  const {
    fields: capitalRangeFields,
    append: appendCapitalRange,
    remove: removeCapitalRange,
  } = useFieldArray({ control: form.control, name: "capital_ranges" });

  // Validação por etapa
  const validateTab = React.useCallback(
    async (tab: string): Promise<boolean> => {
      const fieldsByTab: Record<string, string[]> = {
        data: [
          "name",
          "insurance_company_id",
          "policy_number",
          "susep_code",
          "type_of_vigency",
          "policy_start_date",
          "policy_end_date",
          "min_permitted_age",
          "max_permitted_age",
        ],
        price: ["capital_ranges"],
        commission: ["commission_distribution"],
        integration: [],
      };

      const fieldsToValidate = fieldsByTab[tab] || [];
      if (fieldsToValidate.length === 0) return true;

      const result = await form.trigger(fieldsToValidate as any);
      return result;
    },
    [form]
  );

  const handleTabChange = React.useCallback(
    async (value: string) => {
      const tabOrder = ["data", "price", "commission", "integration"];
      const currentIndex = tabOrder.indexOf(activeTab);
      const newIndex = tabOrder.indexOf(value);

      // Permite voltar para tabs anteriores sem validar
      if (newIndex < currentIndex) {
        setActiveTab(value);
        return;
      }

      // Valida antes de avançar
      const isValid = await validateTab(activeTab);
      if (isValid) {
        setActiveTab(value);
      } else {
        // Mostra mensagem de erro
        form.trigger(
          activeTab === "data"
            ? ([
                "name",
                "insurance_company_id",
                "policy_number",
                "susep_code",
                "type_of_vigency",
                "policy_start_date",
                "policy_end_date",
                "min_permitted_age",
                "max_permitted_age",
              ] as any)
            : activeTab === "price"
            ? (["capital_ranges"] as any)
            : (["commission_distribution"] as any)
        );
      }
    },
    [activeTab, validateTab, form]
  );
  const addInput = () => {
    appendCapitalRange({
      capital_value: 0,
      premium_value: 0,
      description: "",
      markup: 0,
      markup_mode: "percentage",
      practiced_price_type: "minimum",
      minimum_markup: 0,
      maximum_markup: 0,
      practiced_price: 0,
      iof_value: 0,
    } as any);
  };

  const removeInput = (index: number) => {
    if (capitalRangeFields.length > 1) {
      removeCapitalRange(index);
    }
  };

  const { insuranceCompanies } = useInsuranceCompanies();

  // Reset tab quando o formulário for aberto
  React.useEffect(() => {
    if (open) {
      setActiveTab("data");
    }
  }, [open]);

  if (open) {
    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => {
            onSubmit(data);
          })}
          className="space-y-4"
        >
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="data">
                1. {t("products.title.tab.data")}
              </TabsTrigger>
              <TabsTrigger value="price">
                2. {t("products.title.tab.plans")}
              </TabsTrigger>
              <TabsTrigger value="commission">
                3. {t("products.title.tab.commercialLoading")}
              </TabsTrigger>
              <TabsTrigger value="integration">
                4. {t("products.title.tab.integration")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="data">
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="name" required>
                          {t("products.form.fields.name")}
                        </RequiredLabel>
                        <FormControl>
                          <Input {...field} id="name" placeholder=" " />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <SoulhubExternalProductIdField form={form} />
                  <FormField
                    control={form.control}
                    name="insurance_company_id"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="insurance_company_id" required>
                          {t("products.form.fields.insurance_company_id")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-[100%]">
                              <SelectValue
                                placeholder={t(
                                  "products.form.fields.insurance_company_id.placeholder"
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {insuranceCompanies.map((company) => (
                                  <SelectItem
                                    key={company.id}
                                    value={company.id || ""}
                                  >
                                    {company.name} - {company.cnpj}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <PaymentGatewayField control={form.control} />
                  <FormField
                    control={form.control}
                    name="policy_number"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="policy_number" required>
                          {t("products.form.fields.policy_number")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="policy_number"
                            placeholder=" "
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cod_sub"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="cod_sub">
                          {t("products.form.fields.cod_sub")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="cod_sub"
                            placeholder=" "
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="susep_code"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="susep_code" required>
                          {t("products.form.fields.susep_code")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="susep_code"
                            placeholder=" "
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="type_of_vigency"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="type_of_vigency" required>
                          {t("products.form.fields.type_of_vigency")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-[100%]">
                              <SelectValue
                                placeholder={t(
                                  "products.form.fields.type_of_recurrence.placeholder"
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="lifetime">
                                  {t("products.form.fields.lifetime")}
                                </SelectItem>
                                <SelectItem value="annual">
                                  {t("products.form.fields.annual")}
                                </SelectItem>
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="iof_value"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="iof_value" required>
                          {t("products.form.fields.iof_value")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="iof_value"
                            placeholder="0,00"
                            type="number"
                            min={0}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="policy_start_date"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="policy_start_date" required>
                          {t("products.form.fields.policy_start_date")}
                        </RequiredLabel>
                        <FormControl>
                          <DatePicker
                            value={field.value || undefined}
                            onChange={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="policy_end_date"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="policy_end_date" required>
                        {t("products.form.fields.policy_end_date")}
                        </RequiredLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value || undefined}
                              onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="min_permitted_age"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="min_permitted_age" required>
                          {t("products.form.fields.min_permitted_age")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="min_permitted_age"
                            type="number"
                            placeholder=" "
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="max_permitted_age"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="max_permitted_age" required>
                          {t("products.form.fields.max_permitted_age")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="max_permitted_age"
                            type="number"
                            placeholder=" "
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.form.fields.description")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="insurance_coverages"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.form.fields.coverage")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="insurance_benefits"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.form.fields.benefits")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="general_conditions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="general_conditions">
                        {t("products.form.fields.general_conditions")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Card>
            </TabsContent>
            <TabsContent value="price">
              <Card className="p-4">
                <div className="flex flex-wrap gap-4 mb-4">
                  {capitalRangeFields.map((fieldItem, index) => (
                    <Card
                      key={fieldItem.id}
                      className="p-4 flex flex-row gap-4"
                    >
                      <FormField
                        control={form.control}
                        name={`capital_ranges.${index}.capital_value`}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel htmlFor="capital_value" required>
                              {t("products.form.fields.capital_value")}
                            </RequiredLabel>
                            <FormControl>
                              <RealCurrencyInput
                                id="capital_value"
                                value={field.value || 0}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`capital_ranges.${index}.premium_value`}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel htmlFor="premium_value" required>
                              {t("products.form.fields.premium_value")}
                            </RequiredLabel>
                            <FormControl>
                              <RealCurrencyInput
                                id="premium_value"
                                value={field.value || 0}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`capital_ranges.${index}.net_premium`}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel htmlFor="net_premium" required>
                              {t("products.form.fields.net_premium")}
                            </RequiredLabel>
                            <FormControl>
                              <RealCurrencyInput
                                id="net_premium"
                                value={field.value || 0}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </Card>
                  ))}
                </div>{" "}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={addInput}>
                    <Plus className="w-4 h-4" />
                  </Button>

                  {capitalRangeFields.length > 1 && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => removeInput(capitalRangeFields.length - 1)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </Card>
            </TabsContent>
            <TabsContent value="commission">
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {form.watch("type_of_vigency") === "annual" && (
                    <Card className="p-4">
                      <CardTitle>
                        {t("products.form.fields.commission_unique")}
                      </CardTitle>
                      <FormField
                        control={form.control}
                        name="commission_unique"
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel htmlFor="commission_unique" required>
                              {t("products.form.fields.comission")}
                            </RequiredLabel>
                            <FormControl>
                              <NumericInput
                                id="comission_unique"
                                value={field.value || 0}
                                onChange={field.onChange}
                                placeholder="0,00"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="pro_labore_unique"
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel htmlFor="pro_labore_unique" required>
                              {t("products.form.fields.pro_labore")}
                            </RequiredLabel>
                            <FormControl>
                              <NumericInput
                                id="pro_labore_unique"
                                value={field.value || 0}
                                onChange={field.onChange}
                                placeholder="0,00"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="markup"
                        render={({ field }) => (
                          <FormItem className="w-full">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="markup">
                                {form.watch("markup_mode") === "real_currency"
                                  ? t("products.form.fields.markupValue")
                                  : t("products.form.fields.markup")}
                              </Label>
                              <div className="flex items-center gap-2">
                                <FormField
                                  control={form.control}
                                  name="markup_mode"
                                  render={({ field: modeField }) => (
                                    <FormControl>
                                      <div className="flex items-center gap-1">
                                        <Percent className="w-3 h-3 text-muted-foreground" />
                                        <Switch
                                          checked={
                                            modeField.value === "real_currency"
                                          }
                                          onCheckedChange={(checked) => {
                                            modeField.onChange(
                                              checked
                                                ? "real_currency"
                                                : "percentage"
                                            );
                                          }}
                                        />
                                        <DollarSign className="w-3 h-3 text-muted-foreground" />
                                      </div>
                                    </FormControl>
                                  )}
                                />
                              </div>
                            </div>
                            <FormControl>
                              {form.watch("markup_mode") === "real_currency" ? (
                                <RealCurrencyInput
                                  id="markup"
                                  value={field.value || 0}
                                  onChange={field.onChange}
                                />
                              ) : (
                                <NumericInput
                                  id="markup"
                                  value={field.value || 0}
                                  onChange={field.onChange}
                                  placeholder="0,00"
                                  className="w-full"
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </Card>
                  )}
                </div>
                <div>
                  {form.watch("type_of_vigency") === "lifetime" && (
                    <DynamicInputList form={form} />
                  )}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="configurations">
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"></div>
              </Card>
            </TabsContent>

            <TabsContent value="integration">
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="insurance_policy_id"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="insurance_policy_id">
                          {t("products.form.fields.insurance_policy_id")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="insurance_policy_id"
                            placeholder=" "
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="secondary_policy_holder"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="secondary_policy_holder" required>
                          {t("products.form.fields.secondary_policy_holder")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="secondary_policy_holder"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="stipulant_code"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="stipulant_code" required>
                          {t("products.form.fields.stipulant_code")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="stipulant_code"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sub_stipulant_code"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="sub_stipulant_code" required>
                          {t("products.form.fields.sub_stipulant_code")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="sub_stipulant_code"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="table_code"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="table_code" required>
                          {t("products.form.fields.table_code")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="table_code"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* <FormField
                    control={form.control}
                    name="code_plan"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="code_plan">
                          {t("products.form.fields.code_plan")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="code_plan"
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  /> */}
                </div>
                <div className="flex justify-end">
                  <Button className="mt-8" type="submit">
                    {isEditMode
                      ? t("products.button.update")
                      : t("products.button.create")}
                  </Button>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    );
  }
};
